// Mirrors backend/services/csv_dates.py so the CSV import preview shows
// exactly how each expiry date will be read before anything is saved.

export const DATE_ORDER_OPTIONS = [
  { value: "DMY", label: "DD/MM/YYYY (NZ, AU, UK)" },
  { value: "MDY", label: "MM/DD/YYYY (US)" },
  { value: "YMD", label: "YYYY-MM-DD" },
];

// Day/month order of this computer's locale — "the local system" default.
export function detectDateOrder() {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "2-digit", day: "2-digit" })
      .formatToParts(new Date(2026, 11, 31))
      .map((p) => p.type)
      .filter((t) => t === "day" || t === "month" || t === "year");
    const key = parts.map((t) => t[0].toUpperCase()).join("");
    if (key === "MDY" || key === "YMD" || key === "DMY") return key;
  } catch (e) {
    // fall through
  }
  return "DMY";
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

function build(y, m, d) {
  const yy = y.length <= 2 ? 2000 + Number(y) : Number(y);
  const mm = Number(m);
  const dd = Number(d);
  const dt = new Date(yy, mm - 1, dd);
  if (dt.getFullYear() !== yy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  return dt;
}

// Returns { ok: true, date } | { ok: true, date: null } (empty) | { ok: false }
export function previewCsvDate(raw, order) {
  const s = (raw || "").trim();
  if (!s) return { ok: true, date: null };
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/);
  if (iso) {
    const dt = build(iso[1], iso[2], iso[3]);
    return dt ? { ok: true, date: dt } : { ok: false };
  }
  const parts = s.split(/[\s/.\-,]+/).filter(Boolean);
  if (parts.length !== 3) return { ok: false };
  const words = parts.map((p, i) => (/^[a-z]+$/i.test(p) ? i : -1)).filter((i) => i >= 0);
  if (words.length === 1) {
    const month = MONTHS.indexOf(parts[words[0]].slice(0, 3).toLowerCase()) + 1;
    const nums = parts.filter((_, i) => i !== words[0]);
    if (!month || !nums.every((n) => /^\d+$/.test(n))) return { ok: false };
    const [y, d] = nums[0].length === 4 ? nums : [nums[1], nums[0]];
    const dt = build(y, String(month), d);
    return dt ? { ok: true, date: dt } : { ok: false };
  }
  if (!parts.every((p) => /^\d+$/.test(p))) return { ok: false };
  let y, m, d;
  if (parts[0].length === 4) [y, m, d] = parts;
  else if (order === "DMY") [d, m, y] = parts;
  else if (order === "MDY") [m, d, y] = parts;
  else return { ok: false };
  const dt = build(y, m, d);
  return dt ? { ok: true, date: dt } : { ok: false };
}

// Display a stored expiry (calendar date, e.g. "2027-03-14T00:00:00Z") in the
// club's chosen order. Reads the YYYY-MM-DD part as written so the browser's
// language/time zone can't flip or shift it.
export function formatClubDate(value, order = "DMY") {
  if (!value) return "";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(value);
  const [, y, mo, d] = m;
  if (order === "MDY") return `${mo}/${d}/${y}`;
  if (order === "YMD") return `${y}-${mo}-${d}`;
  return `${d}/${mo}/${y}`;
}
